/**
 * CounterCool buy box.
 *
 * An offer is one product the shopper buys, such as the CounterCool or a
 * bundle of CounterCool and CoolConnect. The Cart AJAX API takes an `items`
 * array, so one request adds the offer. https://shopify.dev/docs/api/ajax/reference/cart
 *
 * The same request asks for the cart sections, so the drawer repaints without a
 * second request. https://shopify.dev/docs/api/section-rendering
 */
(function () {
  const OFFER_SELECTOR = '[aura-buy-box-offer]';
  const ERROR_SELECTOR = '[aura-buy-box-error]';
  // A button shows its message in the nearest of these. A Component Popup
  // covers the buy box while it is open, so its own message has to be inside
  // the popup to be readable.
  const ERROR_SCOPE_SELECTOR = '[aura-buy-box], [aura-countercool-component-popup]';

  /**
   * Read the line items an offer adds to the cart.
   *
   * @param {HTMLElement} buttonEl - The offer's add to cart button.
   * @returns {Array|null} Cart API line items, or null when the data is unusable.
   */
  const readItems = (buttonEl) => {
    let items = null;

    try {
      items = JSON.parse(buttonEl.dataset.items);
    } catch (error) {
      return null;
    }

    // Do not proceed unless the value is a filled array. The cart would reject
    // the request or add nothing.
    if (!Array.isArray(items) || items.length === 0) {
      return null;
    }

    return items;
  };

  /**
   * Find the cart element that shows what was added.
   *
   * @returns {HTMLElement|null} The cart notification, the cart drawer, or null.
   */
  const getCart = () => {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  };

  /**
   * Show a message inside the scope the button belongs to.
   *
   * @param {HTMLElement} scopeEl - The buy box or popup holding the message element.
   * @param {String} message - The text to show. An empty string hides it.
   */
  const setError = (scopeEl, message) => {
    // A scope can wrap another scope (a Component Popup inside the buy box), so
    // pick the first match whose own nearest scope is this one, not a nested one.
    const errorEl = Array.from(scopeEl.querySelectorAll(ERROR_SELECTOR)).find(
      (candidateEl) => candidateEl.closest(ERROR_SCOPE_SELECTOR) === scopeEl,
    );

    // Do not proceed without a message element. There is nowhere to put the text.
    if (!errorEl) {
      return;
    }

    errorEl.textContent = message;
    errorEl.hidden = message === '';
  };

  /**
   * Lock or unlock an offer button while its request is in flight.
   *
   * @param {HTMLElement} buttonEl - The offer's add to cart button.
   * @param {Boolean} isBusy - True while the request runs.
   */
  const setBusy = (buttonEl, isBusy) => {
    buttonEl.setAttribute('aria-disabled', isBusy);
  };

  /**
   * Add one offer to the cart.
   *
   * @param {HTMLElement} buttonEl - The offer's add to cart button.
   */
  const addOffer = (buttonEl) => {
    const scopeEl = buttonEl.closest(ERROR_SCOPE_SELECTOR);
    const failureMessage = window.accessibilityStrings.buyBoxAddError;
    const items = readItems(buttonEl);

    // Do not proceed without usable items. The request would add nothing and
    // still report success.
    if (!items) {
      setError(scopeEl, failureMessage);
      return;
    }

    const cart = getCart();
    const body = { items: items };

    // Ask for the sections with the add, so the drawer repaints from one response.
    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      cart.setActiveElement(document.activeElement);
    }

    setBusy(buttonEl, true);
    setError(scopeEl, '');

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((response) => {
        // The Cart API reports a rejected add with a `status` key, not an HTTP error.
        if (response.status) {
          setError(scopeEl, response.description || failureMessage);
          return;
        }

        // With no drawer or notification, nothing here can show the new cart.
        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }

        // `renderContents` leaves `is-empty` on the cart element, and its CSS
        // then lays the new line out as the empty state, at no height.
        cart.classList.remove('is-empty');

        cart.renderContents(response);
      })
      .catch(() => {
        setError(scopeEl, failureMessage);
      })
      .finally(() => {
        setBusy(buttonEl, false);
      });
  };

  // The script is deferred, so the buttons are parsed by the time this runs.
  // https://html.spec.whatwg.org/multipage/scripting.html#attr-script-defer
  const buttonEls = Array.from(document.querySelectorAll(OFFER_SELECTOR));

  buttonEls.forEach((buttonEl) => {
    buttonEl.addEventListener('click', () => {
      // Ignore the click while an earlier add runs, so one offer cannot be
      // added twice.
      if (buttonEl.getAttribute('aria-disabled') === 'true') {
        return;
      }

      addOffer(buttonEl);
    });
  });
})();
